import {match} from 'ts-pattern';

import {t, UserFacingCatalogueError} from '../utils/catalogueUi';
import type {AddResult, TrimSwitchRefusal} from '../utils/pricedItemGroupStore';

/**
 * Why a change was not taken: an `add` refusal of the open group, a batch of more operations than
 * the BE takes, or a price change of a stored row, which cannot be in an open group because a price
 * adjustment applies at once (Q3).
 */
export type PricedItemGroupRefusalReason =
  Exclude<AddResult, 'added' | TrimSwitchRefusal> | 'batchTooLarge' | 'priceChangeOfExistingRow';

/**
 * Why a publication switch of a trim and a new colour or package offered on it cannot be in one
 * group (Q7): it names the trim and says what to do. The same text for the add-time refusal and for
 * the refusal of the BE (`trimSwitchWithNewItemRow`).
 */
export const trimSwitchWithNewItemRowMessage = (trimName: string) =>
  t(
    'entity.newVehicle.catalogue.itemGroup.trimSwitchWithNewItemRow',
    '{trim} cannot change its publication status in the same group as a new colour or package offered on it. Confirm or discard the group first, or add the colour or package after the trim change.',
    {trim: trimName}
  );

const refusalMessage = (reason: PricedItemGroupRefusalReason) =>
  match(reason)
    .with('targetTaken', () =>
      t(
        'entity.newVehicle.catalogue.itemGroup.targetTaken',
        'This item already has a change in the group. Confirm or discard the group first.'
      )
    )
    .with('matrixConflict', () =>
      t(
        'entity.newVehicle.catalogue.itemGroup.matrixConflict',
        'New matrix cells and Variant switches cannot be in one group. Confirm or discard the group first.'
      )
    )
    .with('trimInMatrix', () =>
      t(
        'entity.newVehicle.catalogue.itemGroup.trimInMatrix',
        'A trim change and a new Variant matrix cell of the same trim cannot be in one group. Confirm or discard the group first.'
      )
    )
    .with('valueTaken', () =>
      t(
        'entity.newVehicle.catalogue.itemGroup.valueTaken',
        'Another change in the group uses this name or code. Confirm or discard the group first.'
      )
    )
    .with('tooManyChanges', () =>
      t(
        'entity.newVehicle.catalogue.itemGroup.tooManyChanges',
        'The group holds the maximum of 100 changes. Confirm or discard the group first.'
      )
    )
    .with('batchTooLarge', () =>
      t(
        'entity.newVehicle.catalogue.itemGroup.batchTooLarge',
        'This save changes more than 100 items at once. Save fewer items.'
      )
    )
    .with('priceChangeOfExistingRow', () =>
      t(
        'entity.newVehicle.catalogue.itemGroup.priceChangeOfExistingRow',
        'A price change cannot be made while a group is open. Confirm or discard the group first.'
      )
    )
    .exhaustive();

/**
 * A change that an open group refused (spec 7.2), or a batch too large to send (spec 4.8). Nothing
 * was sent and nothing was added; the call site keeps its editor open and shows the message.
 * Every refusal except a batch too large is `business`, so its toast offers no AI bug report.
 */
export class PricedItemGroupRefusal extends UserFacingCatalogueError {
  readonly reason: PricedItemGroupRefusalReason | TrimSwitchRefusal['reason'];

  constructor(refusal: PricedItemGroupRefusalReason | TrimSwitchRefusal) {
    super(
      typeof refusal === 'string'
        ? refusalMessage(refusal)
        : trimSwitchWithNewItemRowMessage(refusal.trim.name)
    );
    this.name = 'PricedItemGroupRefusal';
    this.reason = typeof refusal === 'string' ? refusal : refusal.reason;
    if (this.reason !== 'batchTooLarge') {
      this.errorOrigin = 'business';
    }
  }
}
